import React from 'react';

// Reusable inline-SVG "image" blocks for blog posts, since the site has no
// photography anywhere, so every visual slot is an original diagram/chart
// in the same dark/cyan language as Mat3D/ShowcaseSection, each with a
// real accessible label (role="img" + aria-label) standing in for alt text.

export const H2 = ({ id, children }) => (
  <h2 id={id} className="text-2xl md:text-3xl font-light text-white pt-4 scroll-mt-28">{children}</h2>
);

export const H3 = ({ children }) => (
  <h3 className="text-lg font-medium text-white pt-2">{children}</h3>
);

export const Figure = ({ caption, label, children }) => (
  <figure className="glass rounded-2xl border border-white/10 p-6 my-2" role="img" aria-label={label}>
    {children}
    {caption && <figcaption className="text-white/35 text-xs mt-4 text-center">{caption}</figcaption>}
  </figure>
);

export const StatBarChart = ({ label, bars }) => (
  <Figure caption={label} label={label}>
    <svg viewBox="0 0 320 140" className="w-full h-auto" aria-hidden="true">
      {bars.map((bar, i) => {
        const barWidth = 60;
        const gap = 20;
        const x = 20 + i * (barWidth + gap);
        const maxH = 100;
        const h = (bar.value / 100) * maxH;
        return (
          <g key={bar.label}>
            <rect x={x} y={120 - h} width={barWidth} height={h} rx="6" fill="#2fd6e8" fillOpacity="0.8" />
            <text x={x + barWidth / 2} y={120 - h - 8} textAnchor="middle" fill="#2fd6e8" fontSize="14" fontWeight="700">
              {bar.value}%
            </text>
            <text x={x + barWidth / 2} y={134} textAnchor="middle" fill="#9aa0a6" fontSize="9" textTransform="uppercase">
              {bar.label}
            </text>
          </g>
        );
      })}
    </svg>
  </Figure>
);

export const StepDiagram = ({ steps, label }) => (
  <Figure caption={label} label={label}>
    <div className="flex flex-col gap-3">
      {steps.map((step, i) => (
        <div key={step} className="flex items-center gap-4">
          <span className="w-7 h-7 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold flex items-center justify-center shrink-0">
            {i + 1}
          </span>
          <span className="text-white/55 text-xs md:text-sm">{step}</span>
          {i < steps.length - 1 && <span className="hidden" />}
        </div>
      ))}
    </div>
  </Figure>
);

export const CompareTable = ({ label, columns, rows }) => (
  <Figure label={label}>
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs md:text-sm border-collapse min-w-[420px]">
        <thead>
          <tr className="border-b border-white/10 text-white/60 uppercase tracking-widest text-[10px]">
            {columns.map((col, i) => (
              <th key={col} className={`py-3 pr-4 font-medium ${i === columns.length - 1 ? 'text-cyan-400' : ''}`}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody className="text-white/50">
          {rows.map((row) => (
            <tr key={row[0]} className="border-b border-white/5">
              {row.map((cell, i) => (
                <td key={i} className={`py-3 pr-4 ${i === row.length - 1 ? 'text-cyan-400' : 'text-white/70'}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Figure>
);

export const FeaturedBanner = ({ eyebrow, headline, label }) => (
  <figure
    role="img"
    aria-label={label}
    className="rounded-3xl border border-cyan-500/20 p-10 md:p-14 text-center bg-gradient-to-br from-cyan-500/10 via-transparent to-transparent"
  >
    <p className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4">{eyebrow}</p>
    <p className="text-white text-xl md:text-2xl font-light max-w-md mx-auto">{headline}</p>
  </figure>
);
